import { useState, useEffect } from 'react';
import api from '../services/api';

const BASE = 'https://api.homechamp.in';

export default function Branding() {
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings/app_logo');
      setLogo(res.data.value);
      const allSettings = await api.get('/settings/');
      setSettings(allSettings.data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveSetting = async (key, value) => {
    setSaving(true);
    try {
      await api.put(`/settings/${key}`, { value });
      setSettings(s => ({...s, [key]: value}));
    } catch(e) { alert('Failed to save'); }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/settings/app-logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogo(res.data.value);
      e.target.value = '';
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the app logo?')) return;
    try {
      await api.put('/settings/app_logo', { value: '' });
      setLogo(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <div>
      <h2>🎨 App Branding</h2>
      <p style={{ color: '#888', marginBottom: 24 }}>
        Upload your application logo. It will be shown in the customer and partner apps.
      </p>

      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Application Logo</h3>

        {/* Preview */}
        <div style={previewBox}>
          {loading ? (
            <span style={{ color: '#aaa' }}>Loading...</span>
          ) : logo ? (
            <img src={`${BASE}${logo}`} alt="App Logo"
              style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: 60, opacity: 0.2 }}>🏠</span>
              <p style={{ color: '#aaa', margin: '8px 0 0' }}>No logo uploaded yet</p>
            </div>
          )}
        </div>

        {/* Upload + Remove */}
        <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={btnStyle}>
            {uploading ? '⏳ Uploading...' : '📤 Upload New Logo'}
            <input type="file" accept=".jpg,.jpeg,.jfif,.png,.gif,.webp,.avif,.bmp,.svg,.ico"
              style={{ display: 'none' }}
              onChange={handleUpload}
              disabled={uploading} />
          </label>
          {logo && (
            <button onClick={handleRemove}
              style={{ ...btnStyle, background: '#fee', color: '#d32f2f', border: '1px solid #fcc' }}>
              🗑️ Remove Logo
            </button>
          )}
        </div>

        <p style={{ color: '#999', fontSize: 12, marginTop: 12 }}>
          Supports: JPG, JPEG, JFIF, PNG, GIF, WEBP, AVIF, BMP, SVG, ICO
        </p>

        {logo && (
          <div style={{ marginTop: 12, padding: 12, background: '#f8f9ff', borderRadius: 10 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
              Current: <code style={{ background: '#eee', padding: '2px 6px', borderRadius: 4 }}>{BASE}{logo}</code>
            </p>
          </div>
        )}
      </div>

      {/* Configurable Settings */}
      <div style={{...cardStyle, maxWidth: 600, marginTop: 24}}>
        <h3 style={{ marginTop: 0 }}>⚙️ App Settings</h3>
        <p style={{ color: '#888', fontSize: 12, marginBottom: 16 }}>These values are used by both customer and worker apps without app rebuild.</p>
        <table style={{width:'100%', borderCollapse:'collapse'}}>
          <tbody>
            {[
              {key:'referral_reward_amount', label:'Referral Reward (₹)', desc:'Amount credited to both users'},
              {key:'referral_min_booking_amount', label:'Referral Min Booking (₹)', desc:'Min booking amount for referral to activate'},
              {key:'first_booking_discount', label:'First Booking Discount (₹)', desc:'Discount on first booking'},
              {key:'first_booking_min_amount', label:'First Booking Min Amount (₹)', desc:'Minimum amount to get first booking discount'},
              {key:'auto_cancel_minutes', label:'Auto-Cancel Timeout (mins)', desc:'Cancel unassigned bookings after this time'},
              {key:'worker_reassign_minutes', label:'Worker Reassign Timeout (mins)', desc:'Reassign if worker doesn\'t respond'},
              {key:'contact_phone', label:'Contact Phone', desc:'Shown in app'},
              {key:'contact_email', label:'Contact Email', desc:'Shown in app'},
              {key:'app_version', label:'App Version', desc:'Shown in About section'},
            ].map(({key, label, desc}) => (
              <tr key={key} style={{borderBottom:'1px solid #f0f0f0'}}>
                <td style={{padding:'10px 0', width:'40%'}}>
                  <div style={{fontWeight:600, fontSize:13}}>{label}</div>
                  <div style={{fontSize:11, color:'#999'}}>{desc}</div>
                </td>
                <td style={{padding:'10px 0'}}>
                  <input
                    value={settings[key] || ''}
                    onChange={e => setSettings(s => ({...s, [key]: e.target.value}))}
                    onBlur={e => saveSetting(key, e.target.value)}
                    style={{padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, width:'100%', fontSize:13}}
                  />
                </td>
              </tr>
            ))}
            <tr style={{borderBottom:'1px solid #f0f0f0'}}>
              <td style={{padding:'10px 0', width:'40%'}}>
                <div style={{fontWeight:600, fontSize:13}}>About Text</div>
                <div style={{fontSize:11, color:'#999'}}>Shown in Profile → About</div>
              </td>
              <td style={{padding:'10px 0'}}>
                <textarea
                  value={settings['about_text'] || ''}
                  onChange={e => setSettings(s => ({...s, about_text: e.target.value}))}
                  onBlur={e => saveSetting('about_text', e.target.value)}
                  rows={4}
                  style={{padding:'6px 10px', border:'1px solid #ddd', borderRadius:6, width:'100%', fontSize:13, resize:'vertical'}}
                />
              </td>
            </tr>
          </tbody>
        </table>
        {saving && <p style={{color:'#6C63FF', fontSize:12, marginTop:8}}>Saving...</p>}
      </div>
    </div>
  );
}

const cardStyle = { background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', maxWidth: 500 };
const previewBox = { width: '100%', minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', borderRadius: 12, border: '2px dashed #ddd', padding: 20 };
const btnStyle = { display: 'inline-block', padding: '12px 24px', background: '#6C63FF', color: '#fff', borderRadius: 10, cursor: 'pointer', fontWeight: 'bold', fontSize: 14, textAlign: 'center' };
